import { useCallback, useEffect, useRef } from "react";

import useLiveResource from "@/hooks/useLiveResource";
import { invalidateDedupe } from "@/utils/apiDeduper";

/**
 * Live compliance updates (Phase 2).
 *
 * Compliance is a **shared worklist**: two officers work the same queue, so the
 * backend tenant-broadcasts every mutation and each client refetches. This hook
 * is the single place that translates "an event arrived" into "drop the stale
 * cache, then refetch".
 *
 * ── Why invalidateDedupe FIRST (the subtle part) ──────────────────────────────
 * `dedupeGet` caches the **promise**, not the resolved value, for 2s. A refetch
 * fired inside that window would be handed back the *pre-event* promise and
 * resolve to stale data — the screen would flash "updated" and show the old
 * numbers. So every key a surface reads must be invalidated before `refresh()`
 * runs. There was no existing precedent in the app for WS-driven invalidation
 * when this was written; this hook establishes it.
 *
 * ── Why debounce ─────────────────────────────────────────────────────────────
 * One officer approving a document can emit several events (approve, then the
 * extraction outcome), and the dashboard alone reads four endpoints. Without a
 * debounce, N officers × M events × 4 requests is a refetch storm for one
 * logical change. 300ms matches `NotificationBell.jsx` — long enough to collapse
 * a burst, short enough to feel instant.
 *
 * ── Why WS is never the source of truth ──────────────────────────────────────
 * Events *trigger* a refetch; they are not applied as state (the one exception
 * is the activity feed, which prepends optimistically and is reconciled by the
 * refetch behind it). A dropped event therefore means "stale until the next
 * fetch", never wrong data — the `NotificationBell` doctrine.
 *
 * @param {object}   options
 * @param {Function} options.onRefresh   Called after the caches are dropped.
 * @param {string[]} [options.keys]      dedupeGet keys to invalidate. Pass `[]`
 *   with `clearAll` when the keys are parameterised and unknowable here.
 * @param {boolean}  [options.clearAll]  Clear the whole dedupe cache instead of
 *   named keys — the established blunt route for parameterised keys
 *   (`complianceService.js` does this on writes).
 * @param {string[]} [options.events]    Event names to listen for. Defaults to
 *   every compliance event.
 * @param {Function} [options.filter]    `(payload) => boolean`. Return false to
 *   ignore an event (e.g. a detail screen ignoring other candidates).
 * @param {Function} [options.onEvent]   Called synchronously per accepted event,
 *   **before** the debounce — for optimistic UI like the activity feed.
 * @param {boolean}  [options.enabled=true]
 * @param {number}   [options.debounceMs=300]
 * @returns {{stale: boolean}} true while the socket is down, so a screen can
 *   say so rather than showing numbers it cannot currently keep current.
 */

// Every event the compliance backend emits. Keep in sync with
// `server/app/api/v2/compliance/events.py` — an event missing here is silently
// ignored, which looks exactly like a broken socket.
export const COMPLIANCE_EVENTS = [
  "compliance_candidate.created",
  "compliance_candidate.progress_changed",
  // A re-apply moved the checklist under this candidate: documents reset for
  // re-review, and any upload whose slot the edit removed retired. Listed here
  // as well as reaching the candidate's own portal, because an officer looking
  // at that candidate is watching the same documents change.
  "compliance_candidate.checklist_updated",
  "compliance_document.uploaded",
  // Approve, reject and re-request collapsed into one event: all three change
  // the document's own status, which is what every screen renders.
  "compliance_document.reviewed",
  "compliance_document.extraction_ready",
  "compliance_document.extraction_failed",
  // A candidate rejection settles the case: it drains the worklist, moves the
  // funnel, and can move recruitment applications with it.
  "compliance_candidate.rejected",
  // Config changes. These move thresholds and the approval bar for the whole
  // tenant (a settings save recomputes every candidate's status server-side),
  // so a screen showing derived numbers is stale until it refetches.
  "compliance_settings.changed",
  "compliance_checklist.changed",
];

// The resource namespaces those events are delivered on. The server sends a
// connection only what it subscribed to, so this list — not COMPLIANCE_EVENTS —
// is what decides whether anything arrives at all.
export const COMPLIANCE_KEYS = ["compliance_candidate", "compliance_document", "compliance_checklist", "compliance_settings"];

const useComplianceLiveUpdates = ({ onRefresh, keys = [], clearAll = false, events = COMPLIANCE_EVENTS, filter, onEvent, enabled = true, debounceMs = 300 } = {}) => {
  // Refs so the socket subscription never needs to re-bind when a caller passes
  // a fresh inline callback or array on each render.
  const onRefreshRef = useRef(onRefresh);
  const filterRef = useRef(filter);
  const onEventRef = useRef(onEvent);
  const keysRef = useRef(keys);
  const timerRef = useRef(null);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
    filterRef.current = filter;
    onEventRef.current = onEvent;
    keysRef.current = keys;
  }, [onRefresh, filter, onEvent, keys]);

  // A live socket that outlives the component would refetch into an unmounted
  // tree; the timer is cleared on unmount for the same reason.
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const handle = useCallback(
    (payload) => {
      if (!enabled) return;

      const eventName = payload?.event;
      if (eventName && !events.includes(eventName)) return;
      if (filterRef.current && !filterRef.current(payload)) return;

      // Optimistic hook (activity feed) runs per event, undebounced — dropping
      // intermediate events here would lose feed rows the user should see.
      try {
        onEventRef.current?.(payload);
      } catch {
        // An optimistic-render failure must never stop the authoritative
        // refetch below from happening.
      }

      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        // Order matters — see the header note. Invalidate, then refetch.
        if (clearAll) invalidateDedupe();
        else keysRef.current.forEach((k) => invalidateDedupe(k));
        onRefreshRef.current?.();
      }, debounceMs);
    },
    [enabled, events, clearAll, debounceMs],
  );

  // Subscribe by resource namespace, not per event: the server delivers a
  // connection only what it subscribed to, so the four keys are what make any
  // of this arrive, and adding an event to COMPLIANCE_EVENTS needs no new
  // subscription. `onEvent` rather than `onChange` because the debounce and the
  // cache invalidation above are this hook's own — see the header note on
  // ordering, which `useLiveResource` does not do.
  return useLiveResource({
    keys: COMPLIANCE_KEYS,
    onEvent: handle,
    enabled,
  });
};

export default useComplianceLiveUpdates;
